// src/lib/advisorIntakeModel.js
//
// Ticket 071 (advisor reader). Pure: maps the server's allow-listed `intake` view
// (get-advisor-subject) and the pipeline's light `intake` summary to what the advisor
// screens draw. No React, no network, no policy: the server already decided what the
// advisor may see (consent, saved version, placeholder strip). This only words it.
//
// Wording and logic are the client intake's own, imported not copied:
//   allAnswersFromFacts / buildReviewAnswers  the client's answers in plain words (the
//                                            move-date placeholder prints as "Lived here
//                                            since before {year}", never as a date)
//   buildResultsView / notAssessed            the estimate state and the "not assessed"
//                                            lines (REVIEW_ROW_LABELS, the impact lines)
//   INTAKE_ASKED_FACTS (via screensAsking)    which open facts the intake really asks

import { allAnswersFromFacts } from './intake/section23Facts'
import { buildReviewAnswers } from './intake/reviewAnswers'
import { buildResultsView } from './intake/resultsModel'
import { screensAsking } from './intake/askedFacts'

export const ADVISOR_INTAKE_TITLE = 'Shared intake'
export const ADVISOR_INTAKE_CONTACT_TITLE = 'Contact'
export const ADVISOR_INTAKE_GAPS_TITLE = 'Not provided by the client'
export const ADVISOR_INTAKE_GAPS_NONE = 'Nothing is missing from the answers the intake asks for.'
export const ADVISOR_INTAKE_GAP_GROUPS = {
  skipped: 'Skipped income',
  notSure: 'Not sure',
  notAssessed: 'Not assessed',
}
export const ADVISOR_INTAKE_ANSWERS_TITLE = "Client's answers"
export const ADVISOR_INTAKE_ANSWER_SECTIONS = { 1: 'About the client', 2: 'Income, property and assets' }
export const ADVISOR_INTAKE_RESULTS_TITLE = 'What the engine produced'
export const ADVISOR_INTAKE_RESULTS_NOTE = 'Calculated by the intake engine from the client\'s saved answers. Estimates, not a tax filing.'
export const ADVISOR_INTAKE_ESTIMATE_HEADING = 'IRPF estimate'
export const ADVISOR_INTAKE_SNAPSHOT_HEADING = 'Spain tax picture'
export const ADVISOR_INTAKE_AUTONOMO_HEADING = 'Autónomo'
export const ADVISOR_INTAKE_NO_ESTIMATE = 'No IRPF estimate for this client'
export const ADVISOR_INTAKE_FINDINGS_STILL_APPLY = 'The Snapshot findings above still apply.'
export const ADVISOR_INTAKE_STANDOUT_HEADING = 'What stands out'
export const ADVISOR_INTAKE_ONLY_NOTE = 'This client has shared an intake and has no Snapshot or Calculator profile, so there is no case brief to generate.'

const shortDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const savedLine = (savedAt, versionNumber) => {
  const date = shortDate(savedAt)
  const version = versionNumber ? `version ${versionNumber}` : null
  if (date && version) return `Saved ${date}, ${version}`
  return date ? `Saved ${date}` : (version ? `Saved, ${version}` : 'Saved')
}

export const estimateShownLine = (clientSawEstimate) =>
  (clientSawEstimate ? 'The client was shown the estimate.' : 'The client was not shown the estimate.')

export const engineLine = (engineVersion) => (engineVersion ? `Engine version ${engineVersion}` : null)

// intake: the server view. today: the saved date, so "before {cutoff year}" matches the
// moment the client saved, not the moment the advisor looks.
export function buildAdvisorIntakeModel(intake) {
  const savedAtDate = intake && intake.saved_at ? new Date(intake.saved_at) : new Date()
  const today = Number.isNaN(savedAtDate.getTime()) ? new Date() : savedAtDate
  const facts = intake.facts || {}
  const results = intake.results || {}

  const answers = allAnswersFromFacts(facts, today)
  const ctx = { answers, suggestions: null, dismissed: [] }
  const rows = buildReviewAnswers({ answers, taxYear: intake.tax_year, today })
  const comunidadFact = facts.household && facts.household.comunidad_autonoma
  const comunidad = comunidadFact && comunidadFact.status === 'known_value' ? comunidadFact.value : null
  const view = buildResultsView({ results, ctx, comunidad })

  const skipped = rows.filter((r) => r.status === 'skipped')
  const notSure = rows.filter((r) => r.status === 'not_sure')
  const notAssessedLines = view.notAssessed

  return {
    taxYear: intake.tax_year,
    savedLine: savedLine(intake.saved_at, intake.version_number),
    engineLine: engineLine(intake.engine_version),
    estimateLine: estimateShownLine(intake.client_saw_estimate),
    contact: intake.contact || { name: null, phone: null },
    gaps: { skipped, notSure, notAssessed: notAssessedLines },
    hasGaps: skipped.length + notSure.length + notAssessedLines.length > 0,
    sections: [1, 2]
      .map((n) => ({ section: n, title: ADVISOR_INTAKE_ANSWER_SECTIONS[n], rows: rows.filter((r) => r.section === n) }))
      .filter((s) => s.rows.length > 0),
    view,
  }
}

// ── Pipeline (light summary) ────────────────────────────────────────────────────

// How many screens' worth of asked facts are still open. The pipeline sends compact
// (domain, fact, status) lines; the allow-list of facts the intake really asks lives only
// in the browser bundle, so the count is made here. A skipped income is its own chip.
export function countNotAssessed(openFacts, gaps) {
  const screens = new Set()
  for (const f of [...(openFacts || []), ...(gaps || [])]) {
    if (f.status === 'unknown' && f.domain === 'income' && (f.fact === 'self_income_items' || f.fact === 'spouse_income_items')) continue
    const asking = screensAsking(`${f.domain}.${f.fact}`)
    if (asking.length > 0) screens.add(asking[0])
  }
  return screens.size
}
